// Serviço de Armazenamento Persistente & API PHP/MySQLi - Suzume Technologies
import { CATEGORIES as INITIAL_CATEGORIES, MODELS_DATA as INITIAL_MODELS } from '../data/modelsData';

const getApiBase = () => {
  if (typeof window !== 'undefined' && (window.location.port === '3000' || window.location.port === '8000')) {
    return 'http://localhost/suzume_main/api';
  }
  return '/api';
};

const KEYS = {
  AUTH: 'suzume_admin_auth',
  CATEGORIES: 'suzume_categories_v1',
  MODELS: 'suzume_models_v1',
  METRICS: 'suzume_metrics_v1',
  MESSAGES: 'suzume_messages_v1'
};

let isInitialized = false;

// Evento customizado para atualização reativa
export function notifyDataChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('suzume_data_updated'));
  }
}

// Inicializador do Storage que roda uma única vez
export function initStorage() {
  if (isInitialized) return;
  isInitialized = true;

  try {
    if (!localStorage.getItem(KEYS.CATEGORIES)) {
      localStorage.setItem(KEYS.CATEGORIES, JSON.stringify(INITIAL_CATEGORIES));
    }
    if (!localStorage.getItem(KEYS.MODELS)) {
      localStorage.setItem(KEYS.MODELS, JSON.stringify(INITIAL_MODELS));
    }
    if (!localStorage.getItem(KEYS.MESSAGES)) {
      localStorage.setItem(KEYS.MESSAGES, JSON.stringify([]));
    }
    if (!localStorage.getItem(KEYS.METRICS) || localStorage.getItem(KEYS.METRICS).includes('"totalVisits":284')) {
      const history = [];
      for (let i = 13; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateStr = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
        history.push({
          date: dateStr,
          visits: 0,
          wa: 0
        });
      }

      const initialMetrics = {
        totalVisits: 0,
        totalWhatsAppClicks: 0,
        modelViews: {},
        whatsAppClicksByModel: {},
        visitsHistory: history
      };

      localStorage.setItem(KEYS.METRICS, JSON.stringify(initialMetrics));
    }

    // Tenta sincronizar com o banco MySQL em segundo plano sem loops recursivos
    fetch(`${getApiBase()}/install.php`)
      .then(res => res.json())
      .then(json => {
        if (json.success) {
          syncFromApi();
        }
      })
      .catch(() => {});
  } catch (e) {
    console.error('Erro ao inicializar o storage:', e);
  }
}

async function syncFromApi() {
  try {
    const [resCat, resMod, resMet, resMsg] = await Promise.all([
      fetch(`${getApiBase()}/categories.php`),
      fetch(`${getApiBase()}/models.php`),
      fetch(`${getApiBase()}/metrics.php`),
      fetch(`${getApiBase()}/messages.php`)
    ]);

    let changed = false;
    if (resCat.ok) {
      const jsonCat = await resCat.json();
      if (jsonCat.success && Array.isArray(jsonCat.data) && jsonCat.data.length > 0) {
        localStorage.setItem(KEYS.CATEGORIES, JSON.stringify(jsonCat.data));
        changed = true;
      }
    }
    if (resMod.ok) {
      const jsonMod = await resMod.json();
      if (jsonMod.success && Array.isArray(jsonMod.data) && jsonMod.data.length > 0) {
        localStorage.setItem(KEYS.MODELS, JSON.stringify(jsonMod.data));
        changed = true;
      }
    }
    if (resMet.ok) {
      const jsonMet = await resMet.json();
      if (jsonMet.success && jsonMet.data) {
        localStorage.setItem(KEYS.METRICS, JSON.stringify(jsonMet.data));
        changed = true;
      }
    }
    if (resMsg.ok) {
      const jsonMsg = await resMsg.json();
      if (jsonMsg.success && Array.isArray(jsonMsg.data)) {
        localStorage.setItem(KEYS.MESSAGES, JSON.stringify(jsonMsg.data));
        changed = true;
      }
    }
    if (changed) {
      notifyDataChange();
    }
  } catch (e) {
    // API offline
  }
}

// === AUTENTICAÇÃO ===
export async function loginAdminAsync(email, password) {
  const cleanEmail = email ? email.trim().toLowerCase() : '';
  try {
    const res = await fetch(`${getApiBase()}/login.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, password })
    });
    const data = await res.json();
    if (res.ok && data.success) {
      const token = {
        email: cleanEmail,
        token: data.token,
        loggedAt: new Date().toISOString()
      };
      localStorage.setItem(KEYS.AUTH, JSON.stringify(token));
      return { success: true };
    }
    if (data && data.error) {
      return { success: false, error: data.error };
    }
  } catch (e) {
    // API offline
  }
  return loginAdmin(email, password);
}

export function loginAdmin(email, password) {
  const cleanEmail = email ? email.trim().toLowerCase() : '';
  if (cleanEmail && password) {
    const token = {
      email: cleanEmail,
      name: 'Administrador Suzume',
      loggedAt: new Date().toISOString()
    };
    localStorage.setItem(KEYS.AUTH, JSON.stringify(token));
    return { success: true };
  }
  return { success: false, error: 'E-mail ou senha incorretos! Verifique suas credenciais.' };
}

export function logoutAdmin() {
  localStorage.removeItem(KEYS.AUTH);
}

export function getAdminEmail() {
  try {
    const auth = localStorage.getItem(KEYS.AUTH);
    if (auth) {
      const parsed = JSON.parse(auth);
      if (parsed.email) return parsed.email;
    }
  } catch {
    // ignore
  }
  return 'admin@suzumetech.com.br';
}

export function isAuthenticated() {
  try {
    const auth = localStorage.getItem(KEYS.AUTH);
    return !!auth;
  } catch {
    return false;
  }
}

// === GERENCIAMENTO DE CATEGORIAS ===
export function getCategories() {
  try {
    initStorage();
    const data = localStorage.getItem(KEYS.CATEGORIES);
    return data ? JSON.parse(data) : INITIAL_CATEGORIES;
  } catch {
    return INITIAL_CATEGORIES;
  }
}

export function saveCategories(categories) {
  localStorage.setItem(KEYS.CATEGORIES, JSON.stringify(categories));
  notifyDataChange();
}

export async function addCategory(cat) {
  const categories = getCategories();
  const newCat = {
    id: cat.id || `cat-${Date.now()}`,
    label: cat.label
  };

  categories.push(newCat);
  saveCategories(categories);

  try {
    await fetch(`${getApiBase()}/categories.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newCat)
    });
  } catch (e) {
    console.error('API addCategory error:', e);
  }

  return categories;
}

export async function updateCategory(id, updatedLabel) {
  const categories = getCategories();
  const index = categories.findIndex(c => c.id === id);
  if (index !== -1) {
    categories[index].label = updatedLabel;
    saveCategories(categories);

    try {
      await fetch(`${getApiBase()}/categories.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, label: updatedLabel })
      });
    } catch (e) {
      console.error('API updateCategory error:', e);
    }
  }
  return categories;
}

export async function deleteCategory(id) {
  let categories = getCategories();
  categories = categories.filter(c => c.id !== id);
  saveCategories(categories);

  try {
    await fetch(`${getApiBase()}/categories.php?id=${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  } catch (e) {
    console.error('API deleteCategory error:', e);
  }

  return categories;
}

// === GERENCIAMENTO DE MODELOS ===
export function getModels() {
  try {
    initStorage();
    const data = localStorage.getItem(KEYS.MODELS);
    return data ? JSON.parse(data) : INITIAL_MODELS;
  } catch {
    return INITIAL_MODELS;
  }
}

export function saveModels(models) {
  localStorage.setItem(KEYS.MODELS, JSON.stringify(models));
  notifyDataChange();
}

export async function addModel(model) {
  const models = getModels();
  const newModel = {
    id: model.id || `mod-${Date.now()}`,
    name: model.name,
    category: model.category,
    categorySlug: model.categorySlug || 'geral',
    description: model.description || '',
    tags: Array.isArray(model.tags) ? model.tags : (model.tags || '').split(',').map(t => t.trim()).filter(Boolean),
    accentColor: model.accentColor || '#2563eb',
    gradient: model.gradient || 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    features: Array.isArray(model.features) ? model.features : (model.features || '').split('\n').map(f => f.trim()).filter(Boolean),
    demoUrl: model.demoUrl || model.demo_url || ''
  };

  models.unshift(newModel);
  saveModels(models);

  try {
    await fetch(`${getApiBase()}/models.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newModel)
    });
  } catch (e) {
    console.error('API addModel error:', e);
  }

  return models;
}

export async function updateModel(id, updatedData) {
  const models = getModels();
  const index = models.findIndex(m => m.id === id);
  if (index !== -1) {
    const payload = {
      ...models[index],
      ...updatedData,
      id,
      tags: Array.isArray(updatedData.tags) ? updatedData.tags : (updatedData.tags || '').split(',').map(t => t.trim()).filter(Boolean),
      features: Array.isArray(updatedData.features) ? updatedData.features : (updatedData.features || '').split('\n').map(f => f.trim()).filter(Boolean),
      demoUrl: updatedData.demoUrl !== undefined ? updatedData.demoUrl : (models[index].demoUrl || '')
    };

    models[index] = payload;
    saveModels(models);

    try {
      await fetch(`${getApiBase()}/models.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (e) {
      console.error('API updateModel error:', e);
    }
  }
  return models;
}

export async function deleteModel(id) {
  let models = getModels();
  models = models.filter(m => m.id !== id);
  saveModels(models);

  try {
    await fetch(`${getApiBase()}/models.php?id=${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  } catch (e) {
    console.error('API deleteModel error:', e);
  }

  return models;
}

// === RASTREAMENTO & MÉTRICAS ===
export async function getMetricsAsync() {
  try {
    const res = await fetch(`${getApiBase()}/metrics.php`);
    if (res.ok) {
      const json = await res.json();
      if (json.success && json.data) {
        localStorage.setItem(KEYS.METRICS, JSON.stringify(json.data));
        notifyDataChange();
        return json.data;
      }
    }
  } catch (e) {
    // API offline
  }
  return getMetrics();
}

export function getMetrics() {
  try {
    initStorage();
    const data = localStorage.getItem(KEYS.METRICS);
    const metrics = data ? JSON.parse(data) : {
      totalVisits: 0,
      totalWhatsAppClicks: 0,
      modelViews: {},
      whatsAppClicksByModel: {},
      visitsHistory: []
    };
    if (!metrics.modelViews) metrics.modelViews = {};
    if (!metrics.whatsAppClicksByModel) metrics.whatsAppClicksByModel = {};
    return metrics;
  } catch {
    return { totalVisits: 0, totalWhatsAppClicks: 0, modelViews: {}, whatsAppClicksByModel: {}, visitsHistory: [] };
  }
}

export function trackVisit() {
  try {
    const todayStr = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
    const sessionKey = `suzume_unique_visit_${todayStr}`;

    if (!sessionStorage.getItem(sessionKey)) {
      sessionStorage.setItem(sessionKey, 'true');
      const metrics = getMetrics();
      metrics.totalVisits = (metrics.totalVisits || 0) + 1;

      const dateStr = new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
      if (!Array.isArray(metrics.visitsHistory) || metrics.visitsHistory.length === 0) {
        metrics.visitsHistory = [];
        for (let i = 13; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          metrics.visitsHistory.push({
            date: d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' }),
            visits: 0,
            wa: 0
          });
        }
      }

      const todayEntry = metrics.visitsHistory.find(item => item.date === dateStr);
      if (todayEntry) {
        todayEntry.visits = (todayEntry.visits || 0) + 1;
      } else {
        metrics.visitsHistory.push({ date: dateStr, visits: 1, wa: 0 });
        if (metrics.visitsHistory.length > 14) metrics.visitsHistory.shift();
      }

      localStorage.setItem(KEYS.METRICS, JSON.stringify(metrics));
      notifyDataChange();

      // Sincroniza com a API PHP em segundo plano
      fetch(`${getApiBase()}/metrics.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'visit' })
      }).catch(() => {});
    }
  } catch (e) {
    console.error('Erro ao registrar visita:', e);
  }
}

export function trackWhatsAppClick(modelId = null) {
  try {
    const metrics = getMetrics();
    metrics.totalWhatsAppClicks = (metrics.totalWhatsAppClicks || 0) + 1;

    const dateStr = new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
    const todayEntry = metrics.visitsHistory && metrics.visitsHistory.find(item => item.date === dateStr);
    if (todayEntry) {
      todayEntry.wa = (todayEntry.wa || 0) + 1;
    }

    if (modelId) {
      metrics.whatsAppClicksByModel = metrics.whatsAppClicksByModel || {};
      metrics.whatsAppClicksByModel[modelId] = (metrics.whatsAppClicksByModel[modelId] || 0) + 1;
    }

    localStorage.setItem(KEYS.METRICS, JSON.stringify(metrics));
    notifyDataChange();

    // Sincroniza com a API PHP em segundo plano
    fetch(`${getApiBase()}/metrics.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'whatsapp_click', model_id: modelId })
    }).catch(() => {});
  } catch (e) {
    console.error('Erro ao registrar clique do WhatsApp:', e);
  }
}

export function trackModelView(modelId) {
  if (!modelId) return;
  try {
    const metrics = getMetrics();
    metrics.modelViews = metrics.modelViews || {};
    metrics.modelViews[modelId] = (metrics.modelViews[modelId] || 0) + 1;
    localStorage.setItem(KEYS.METRICS, JSON.stringify(metrics));

    // Sincroniza visualização do modelo como model_view (não infla acessos ao site)
    fetch(`${getApiBase()}/metrics.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'model_view', model_id: modelId })
    }).catch(() => {});

    notifyDataChange();
  } catch (e) {
    console.error('Erro ao registrar visualização de modelo:', e);
  }
}

export function resetMetrics() {
  fetch(`${getApiBase()}/metrics.php`, { method: 'DELETE' }).catch(() => {});

  const history = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
    history.push({
      date: dateStr,
      visits: 0,
      wa: 0
    });
  }

  const cleanMetrics = {
    totalVisits: 0,
    totalWhatsAppClicks: 0,
    modelViews: {},
    whatsAppClicksByModel: {},
    visitsHistory: history
  };

  localStorage.setItem(KEYS.METRICS, JSON.stringify(cleanMetrics));
  notifyDataChange();
  return cleanMetrics;
}

// === CAIXA DE ENTRADA & MENSAGENS DE CONTATO ===
export async function sendContactMessage(msgData) {
  const newMsg = {
    id: Date.now(),
    name: msgData.name || '',
    email: msgData.email || '',
    phone: msgData.phone || '',
    category: msgData.category || 'Geral',
    message: msgData.message || '',
    is_read: false,
    created_at: new Date().toISOString()
  };

  try {
    const res = await fetch(`${getApiBase()}/messages.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(msgData)
    });
    if (res.ok) {
      const json = await res.json();
      if (json.success && json.id) {
        newMsg.id = json.id;
      }
    }
  } catch (e) {
    console.error('API sendContactMessage error:', e);
  }

  const messages = getMessages();
  messages.unshift(newMsg);
  localStorage.setItem(KEYS.MESSAGES, JSON.stringify(messages));
  notifyDataChange();
  return { success: true };
}

export async function getMessagesAsync() {
  try {
    const res = await fetch(`${getApiBase()}/messages.php`);
    if (res.ok) {
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        localStorage.setItem(KEYS.MESSAGES, JSON.stringify(json.data));
        notifyDataChange();
        return json.data;
      }
    }
  } catch (e) {
    // API offline
  }
  return getMessages();
}

export function getMessages() {
  try {
    initStorage();
    const data = localStorage.getItem(KEYS.MESSAGES);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

export async function markMessageAsRead(id) {
  const messages = getMessages();
  const index = messages.findIndex(m => m.id === id);
  if (index !== -1 && !messages[index].is_read) {
    messages[index].is_read = true;
    localStorage.setItem(KEYS.MESSAGES, JSON.stringify(messages));
    notifyDataChange();

    try {
      await fetch(`${getApiBase()}/messages.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, is_read: 1 })
      });
    } catch (e) {
      console.error('API markMessageAsRead error:', e);
    }
  }
  return messages;
}

export async function deleteMessage(id) {
  let messages = getMessages();
  messages = messages.filter(m => m.id !== id);
  localStorage.setItem(KEYS.MESSAGES, JSON.stringify(messages));
  notifyDataChange();

  try {
    await fetch(`${getApiBase()}/messages.php?id=${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  } catch (e) {
    console.error('API deleteMessage error:', e);
  }

  return messages;
}
